import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel, FieldDescription } from '@/components/ui/field';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { PatientFormValues } from '@/features/emr/schemas/patientForm';
import type { PatientMetadata } from '@/features/emr/api/patients-api';
import { usePatientConfiguration, useSavePatientConfiguration } from './api';

export function CustomFieldDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const config = usePatientConfiguration();
  const save = useSavePatientConfiguration();
  const [label, setLabel] = useState('');
  const [options, setOptions] = useState('');
  const [kind, setKind] = useState('text');
  const duplicate = config.data?.fields.some((f) => f.label.toLowerCase() === label.trim().toLowerCase());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create patient column</DialogTitle>
          <DialogDescription>
            This field will be available throughout your practice in patient tables, Record Info and Edit Patient.
          </DialogDescription>
        </DialogHeader>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="custom-label">Column name</FieldLabel>
            <Input id="custom-label" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} />
            {duplicate && <p role="alert">This column name already exists.</p>}
          </Field>
          <Field>
            <FieldLabel>Field type</FieldLabel>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="options">Dropdown</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
          {kind === 'options' && (
            <Field>
              <FieldLabel htmlFor="custom-options">Dropdown options</FieldLabel>
              <Textarea id="custom-options" value={options} onChange={(e) => setOptions(e.target.value)} />
              <FieldDescription>Enter one option per line. Leave patient values blank when unknown.</FieldDescription>
            </Field>
          )}
        </FieldGroup>
        {config.isError && <p role="alert">Could not load practice fields.</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !config.data || !label.trim() || duplicate || save.isPending || (kind === 'options' && !options.trim())
            }
            onClick={async () => {
              try {
                await save.mutateAsync({
                  ...config.data!,
                  fields: [
                    ...config.data!.fields,
                    {
                      id: `field_${crypto.randomUUID()}`,
                      label: label.trim(),
                      options:
                        kind === 'options'
                          ? options
                              .split('\n')
                              .map((s) => s.trim())
                              .filter(Boolean)
                          : [],
                    },
                  ],
                });
                setLabel('');
                setOptions('');
                onOpenChange(false);
                toast.success('Patient column created');
              } catch {
                toast.error('Unable to create column. Check the name and options, then retry.');
              }
            }}>
            Create column
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
export function CustomPatientFields({ form }: { form: UseFormReturn<PatientFormValues> }) {
  const config = usePatientConfiguration();
  if (config.isError) return <p role="alert">Custom patient fields could not be loaded.</p>;
  return (
    <FieldGroup>
      {config.data?.fields.map((def) => (
        <FormField
          key={def.id}
          control={form.control}
          name={`custom_fields.${def.id}`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{def.label}</FormLabel>
              <FormControl>
                {def.options.length ? (
                  <Select
                    value={field.value || '__blank'}
                    onValueChange={(v) => field.onChange(v === '__blank' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="__blank">Not recorded</SelectItem>
                        {field.value && !def.options.includes(field.value) && (
                          <SelectItem value={field.value}>{field.value} (previous option)</SelectItem>
                        )}
                        {def.options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input {...field} value={field.value || ''} maxLength={2000} />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </FieldGroup>
  );
}
export function CustomRecordInfo({ metadata }: { metadata: PatientMetadata }) {
  const config = usePatientConfiguration();
  if (config.isError) return <p role="alert">Custom patient fields could not be loaded.</p>;
  return (
    <dl className="flex flex-col gap-3">
      {config.data?.fields.map((f) => (
        <div key={f.id} className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{f.label}</dt>
          <dd>{metadata.custom_fields?.[f.id] || 'Not recorded'}</dd>
        </div>
      ))}
    </dl>
  );
}
